import { Button } from '@sunwebgroup/uds-ui/button'
import { Dialog } from '@sunwebgroup/uds-ui/dialog'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import * as React from 'react'
import { useIntl } from '@/intl'
import { Custom500 } from '@/pages/500'
import { useCheckoutSid, useRyanairSessionDataQuery } from '@/scopes/checkout/domain/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { RyanairSessionData } from '../../domain/types/ryanairSessionData'
import { CheckoutErrorDialog } from '../CheckoutError/CheckoutErrorDialog'
import { EngineError } from '../EngineError/EngineError'
import { Loader } from '../Layout'
import styles from './RyanairTermsAndConditions.module.css'

interface RyanairSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function RyanairTermsAndConditions({
  isOpen,
  onOpenChange,
}: RyanairSheetProps) {
  const sid = useCheckoutSid()
  const {
    isLoading,
    isFetching,
    data,
    error,
  } = useRyanairSessionDataQuery()
  const intl = useIntl()
  const brandName = useConfiguration('brandName')

  if (!sid) {
    return null
  }

  const actionsSlot = (
    <>
      <Button variant="primary" className={styles.triggerButton} onClick={() => onOpenChange(true)}>
        {intl.formatMessage({
          id: 'checkout_steps_confirm_ryanair_flight_banner_confirm_button_text',
        })}
      </Button>
      <Sheet.Root open={isOpen} onOpenChange={onOpenChange}>
        <Sheet.Body side="bottom" hasPadding={false}>
          <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={intl.formatMessage({ id: 'checkout_steps_confirm_ryanair_flight_banner_iframe_title' })} />
          <Sheet.Content className={styles.sheetContentForIframe}>
            {(isLoading || isFetching) && <Loader />}
            {data?.iFrameUrl && (
              <iframe
                title={intl.formatMessage({ id: 'checkout_steps_confirm_ryanair_flight_banner_iframe_title' })}
                id="ryr-flight-confirm-frame"
                src={data?.iFrameUrl}
                width="100%"
                height="100%"
                sandbox="allow-scripts allow-same-origin"
              >
              </iframe>
            )}
            <ErrorDisplay data={data} error={error} brandName={brandName} />
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>

      <RichText
        className={styles.timerMessage}
        innerHTML={intl.formatMessage({
          id: 'checkout_steps_confirm_ryanair_flight_banner_timer_message',
        })}
      />
    </>

  )
  return (
    <div className={styles.checkoutBoxSection} data-qa="checkout-box-service">
      <Dialog
        icon="AirplaneToRight"
        text={intl.formatMessage({
          id: 'checkout_steps_confirm_ryanair_flight_banner_title',
        })}
        supportingText={intl.formatMessage({
          id: 'checkout_steps_confirm_ryanair_flight_banner_description',
        })}
        type="custom"
        actionsSlot={actionsSlot}
      />
    </div>
  )
}

interface ErrorDisplayProps {
  data?: RyanairSessionData
  error: Error | null
  brandName: string
}

function ErrorDisplay({ data, error, brandName }: ErrorDisplayProps) {
  const intl = useIntl()
  let errorContent = null
  if (data?.error?.hasError) {
    if (!data.error.errorCode && data.error.redirectUrl) {
      // Expired
      const goToPage = () => {
        if (data?.error?.redirectUrl) {
          window.location.href = data.error.redirectUrl
        }
      }
      const buttonData = {
        text: intl.formatMessage({ id: `checkout_steps_error_go_to_${data.error.redirectTo}_button_text` }),
        onClick: goToPage,
      }
      errorContent = <CheckoutErrorDialog buttonData={buttonData} />
    }
    else {
      // Engine error
      errorContent = <EngineError error={data.error} />
    }
  }
  else if (error) {
    // Network or unexpected error
    errorContent = <Custom500 brand={brandName} />
  }
  return errorContent
}
